import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";
import { auth, googleProvider } from "../../config/firebase";
import { accountDeletionsService } from "../../services/db";
import { useToast } from "../../context/ToastContext";
import SEO from "../../components/common/SEO";
import Spinner from "../../components/ui/Spinner";
import transparentLogo from "../../assets/images/Transparent_logo.png";
import { 
  TrashIcon, 
  CheckCircleIcon, 
  ShieldIcon, 
  WarningIcon 
} from "../../components/common/Icons";

const GoogleIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const QUICK_REASONS = [
  { id: "break", desktop: "Taking a break from quizzes", mobile: "Taking a break" },
  { id: "privacy", desktop: "Privacy concerns", mobile: "Privacy concerns" },
  { id: "notifications", desktop: "Too many notifications", mobile: "Notifications" },
  { id: "duplicate", desktop: "Created duplicate account", mobile: "Duplicate account" },
  { id: "technical", desktop: "Technical difficulties", mobile: "Technical issues" },
  { id: "other", desktop: "Other reason", mobile: "Other reason" }
];

const FAQS = [
  {
    q: "How long does account erasure take?",
    a: "Your request enters a 7-day grace period where your account remains intact. After this window concludes, our administration permanently wipes your profile data within 15 business days."
  },
  {
    q: "Can I cancel my deletion request?",
    a: "Yes! At any time during the active 7-day grace period, simply return to this page, sign in with your Google account, and click 'Cancel Request & Keep My Account'."
  },
  {
    q: "Why must I verify with Google?",
    a: "Verification ensures that only the authenticated owner of the account can request permanent erasure, protecting our contestants from unauthorized tampering."
  },
  {
    q: "What happens to my contest scores and leaderboard ranks?",
    a: "All personal references, user identifiers, scores, and trophies tied to your profile will be permanently deleted upon final execution."
  }
];

const UserDeletionRequest = () => {
  const { showToast } = useToast();

  const [user, setUser] = useState(auth.currentUser);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [checkingActive, setCheckingActive] = useState(false);
  const [activeRequest, setActiveRequest] = useState(null);

  const [reason, setReason] = useState("");
  const [selectedChip, setSelectedChip] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [submittedReceipt, setSubmittedReceipt] = useState(null);
  const [copiedId, setCopiedId] = useState("");
  const [openFaq, setOpenFaq] = useState(0);

  // Mobile Collapsible Sidebar State (defaults to closed on mobile for clean screen)
  const [mobileSections, setMobileSections] = useState({
    scope: false,
    faqs: false,
    compliance: false,
  });

  const toggleMobileSection = (key) => {
    setMobileSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Monitor Firebase Auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoadingAuth(false);
    });
    return () => unsubscribe();
  }, []);

  // When user is authenticated, check for an existing active deletion request
  useEffect(() => {
    let isMounted = true;

    const checkActive = async () => {
      if (!user) {
        setActiveRequest(null);
        return;
      }
      setCheckingActive(true);
      try {
        const existing = await accountDeletionsService.getActiveRequestForUser(
          user.uid,
          user.email
        );
        if (isMounted) {
          setActiveRequest(existing || null);
        }
      } catch (err) {
        console.warn("Failed to check active deletion request:", err);
      } finally {
        if (isMounted) {
          setCheckingActive(false);
        }
      }
    };

    if (!loadingAuth) {
      checkActive();
    }

    return () => {
      isMounted = false;
    };
  }, [user, loadingAuth]);

  // Google Sign-In
  const handleGoogleSignIn = async () => {
    try {
      const res = await signInWithPopup(auth, googleProvider);
      setUser(res.user);
      showToast("Account verified successfully.", "success");
    } catch (err) {
      console.error("Google sign in failed:", err);
      if (err.code !== "auth/popup-closed-by-user") {
        showToast("Authentication failed. Please try again.", "error");
      }
    }
  };

  // Switch Account
  const handleSwitchAccount = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setActiveRequest(null);
      setSubmittedReceipt(null);
      setConsent(false);
      setReason("");
      setSelectedChip("");
      showToast("Signed out. Please sign in with the target account.", "info");
    } catch (err) {
      console.error("Sign out error:", err);
    }
  };

  // Select Quick Reason Chip
  const handleSelectChip = (chipText) => {
    if (selectedChip === chipText) {
      setSelectedChip("");
      setReason("");
    } else {
      setSelectedChip(chipText);
      setReason(chipText);
    }
  };

  // Copy Reference ID Helper
  const handleCopyId = (text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text);
    setCopiedId(text);
    showToast("Reference ID copied to clipboard.", "success");
    setTimeout(() => setCopiedId(""), 2500);
  };

  // Submit Deletion Request
  const handleSubmitRequest = async () => {
    if (!consent) {
      showToast("Please acknowledge and check the consent box to proceed.", "error");
      return;
    }
    if (!user) {
      showToast("Please verify your account before submitting.", "error");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        userId: user.uid,
        userEmail: user.email,
        userName: user.displayName || (user.email ? user.email.split("@")[0] : "User"),
        userPhoto: user.photoURL || "",
        reason: reason.trim(),
        gracePeriodDays: 7,
      };

      const result = await accountDeletionsService.create(payload);

      if (result.isExisting) {
        setActiveRequest(result);
        showToast("An active deletion request is already in progress.", "warning");
      } else {
        setSubmittedReceipt(result);
        showToast("Account deletion request submitted successfully.", "success");
      }
    } catch (err) {
      console.error("Submission failed:", err);
      showToast("Failed to submit request. Please try again or contact support.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Cancel Request (Self-service reversal)
  const handleCancelRequest = async (requestId) => {
    if (!requestId || !user) return;

    setCancelling(true);
    try {
      await accountDeletionsService.cancelRequest(requestId, user.uid, user.email);
      setActiveRequest(null);
      setSubmittedReceipt(null);
      setConsent(false);
      setReason("");
      setSelectedChip("");
      showToast("Your deletion request has been cancelled. Your account remains active.", "success");
    } catch (err) {
      console.error("Cancellation failed:", err);
      showToast("Could not cancel request. Please try again or contact support.", "error");
    } finally {
      setCancelling(false);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "Recently";
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "Recently";
    }
  };

  // Calculate current step for Stepper Bar
  // Step 1: Unauthenticated
  // Step 2: Authenticated, no active request
  // Step 3: Active request or submitted receipt
  const currentStep = (!user) ? 1 : (activeRequest || submittedReceipt) ? 3 : 2;

  return (
    <div className="privacy-portal-page">
      <SEO
        title="Account & Personal Data Erasure Portal — Bible Contest"
        description="Official privacy & data safety portal for Bible Contest. Securely verify account ownership, submit data erasure requests, and manage 7-day cancellation grace periods."
        canonicalUrl="/delete-user-request"
      />

      {/* ─── Top Brand Navigation ─── */}
      <header className="privacy-top-navbar">
        <div className="privacy-nav-container">
          <Link to="/" className="privacy-nav-brand">
            <img src={transparentLogo} alt="Bible Contest Logo" className="privacy-nav-logo" />
            <span className="privacy-nav-title">Bible Contest</span>
            <span className="privacy-nav-badge">Privacy Center</span>
          </Link>

          <div className="privacy-nav-links">
            <Link to="/support" className="privacy-nav-link privacy-nav-help">
              Need Help?
            </Link>
            <Link to="/" className="privacy-nav-link" style={{ color: "var(--clr-primary)" }}>
              <span className="desktop-only-inline">Return to App →</span>
              <span className="mobile-only-inline">← App</span>
            </Link>
          </div>
        </div>
      </header>

      {/* ─── Hero Section ─── */}
      <section className="privacy-hero">
        <div className="privacy-hero-pill">
          <ShieldIcon size={14} />
          <span className="desktop-only-inline">Google Play Data Safety & Apple App Store 5.1.1(v) Compliant</span>
          <span className="mobile-only-inline">Verified Privacy Portal</span>
        </div>
        <h1 className="desktop-only-block">Account & Data Erasure Portal</h1>
        <h1 className="mobile-only-block">Delete Account & Data</h1>
        <p className="desktop-only-block">
          We believe you should have complete ownership over your digital footprint. Verify your credentials below to submit a permanent account deletion request with a 7-day safety grace period.
        </p>
        <p className="mobile-only-block">
          Submit your deletion request with an immediate 7-day safety grace window.
        </p>
      </section>

      {/* ─── 3-Step Process Tracker (Desktop) ─── */}
      <div className="privacy-stepper-bar desktop-only-flex">
        <div className="privacy-stepper-track" />
        
        <div className={`privacy-step-item ${currentStep >= 1 ? (currentStep > 1 ? "completed" : "active") : ""}`}>
          <div className="privacy-step-circle">
            {currentStep > 1 ? "✓" : "1"}
          </div>
          <span className="privacy-step-label">Verify Identity</span>
        </div>

        <div className={`privacy-step-item ${currentStep >= 2 ? (currentStep > 2 ? "completed" : "active") : ""}`}>
          <div className="privacy-step-circle">
            {currentStep > 2 ? "✓" : "2"}
          </div>
          <span className="privacy-step-label">Deletion Request</span>
        </div>

        <div className={`privacy-step-item ${currentStep === 3 ? "active" : ""}`}>
          <div className="privacy-step-circle">
            3
          </div>
          <span className="privacy-step-label">7-Day Grace Schedule</span>
        </div>
      </div>

      {/* ─── 3-Step Process Tracker (Mobile Segmented Bar) ─── */}
      <div className="privacy-mobile-stepper mobile-only-block">
        <div className="privacy-mobile-pills">
          <div className={`privacy-mobile-pill ${currentStep >= 1 ? "active" : ""}`} />
          <div className={`privacy-mobile-pill ${currentStep >= 2 ? "active" : ""}`} />
          <div className={`privacy-mobile-pill ${currentStep >= 3 ? "active" : ""}`} />
        </div>
        <div className="privacy-mobile-step-status">
          <span className="privacy-mobile-step-pill-badge">Step {currentStep} of 3</span>
          <span className="privacy-mobile-step-title">
            {currentStep === 1 && "Verify Identity"}
            {currentStep === 2 && "Submit Deletion Request"}
            {currentStep === 3 && "7-Day Grace Schedule"}
          </span>
        </div>
      </div>

      {/* ─── Main Content Grid ─── */}
      <main className="privacy-main-container">
        
        {/* ─── Primary Interactive Card ─── */}
        <section className="privacy-card">
          
          {loadingAuth || checkingActive ? (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <Spinner />
              <p style={{ marginTop: "16px", color: "var(--clr-text-2)", fontSize: "var(--text-sm)", fontWeight: 500 }}>
                {loadingAuth ? "Validating security session..." : "Checking active deletion records..."}
              </p>
            </div>
          ) : submittedReceipt ? (
            /* ─── State: Newly Submitted Receipt ─── */
            <div>
              <div className="privacy-card-header" style={{ textAlign: "center", borderBottom: "none", paddingBottom: 0 }}>
                <div style={{ width: "56px", height: "56px", borderRadius: "50%", background: "#d1e7dd", color: "#0a361b", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: "14px" }}>
                  <CheckCircleIcon size={32} />
                </div>
                <h2 style={{ justifyContent: "center" }}>Deletion Request Confirmed</h2>
                <p>Your account has been scheduled for permanent erasure and has entered its safety grace period.</p>
              </div>

              {/* Receipt Metadata */}
              <div className="privacy-receipt-meta">
                <div className="privacy-meta-row">
                  <span className="privacy-meta-label">Reference ID</span>
                  <span className="privacy-meta-val">
                    <span className="privacy-id-tag">
                      {submittedReceipt.id || submittedReceipt.requestId}
                    </span>
                    <button 
                      type="button" 
                      className="privacy-copy-btn"
                      onClick={() => handleCopyId(submittedReceipt.id || submittedReceipt.requestId)}
                    >
                      {copiedId === (submittedReceipt.id || submittedReceipt.requestId) ? "Copied!" : "Copy"}
                    </button>
                  </span>
                </div>
                <div className="privacy-meta-row">
                  <span className="privacy-meta-label">Verified Email</span>
                  <span className="privacy-meta-val">{submittedReceipt.userEmail || submittedReceipt.email}</span>
                </div>
                <div className="privacy-meta-row">
                  <span className="privacy-meta-label">Submission Date</span>
                  <span className="privacy-meta-val">{formatTimestamp(submittedReceipt.createdAt)}</span>
                </div>
                <div className="privacy-meta-row">
                  <span className="privacy-meta-label">Grace Period</span>
                  <span className="privacy-meta-val" style={{ color: "var(--clr-warning)" }}>
                    7 Days (Reversal Available)
                  </span>
                </div>
              </div>

              {/* Step Timeline Visual */}
              <div className="privacy-timeline-box">
                <div className="privacy-timeline-item">
                  <div className="privacy-timeline-line" />
                  <div className="privacy-timeline-icon phase-1">1</div>
                  <div className="privacy-timeline-text">
                    <h4>Request Logged in Audit Queue</h4>
                    <p>Your request has been securely timestamped and an administrative action item has been dispatched.</p>
                  </div>
                </div>

                <div className="privacy-timeline-item">
                  <div className="privacy-timeline-line" />
                  <div className="privacy-timeline-icon phase-2">2</div>
                  <div className="privacy-timeline-text">
                    <h4>7-Day Safety Grace Period (Active)</h4>
                    <p>Your account is held in an active grace window. You may retract this request at any point before this period ends.</p>
                  </div>
                </div>

                <div className="privacy-timeline-item">
                  <div className="privacy-timeline-icon phase-3">3</div>
                  <div className="privacy-timeline-text">
                    <h4>Permanent Database Erasure (Day 15)</h4>
                    <p>All personal profile documents, scores, and trophies will be permanently removed from Bible Contest servers.</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "24px" }}>
                <button
                  type="button"
                  className="privacy-btn-success"
                  style={{ flex: 1, minWidth: "200px" }}
                  onClick={() => handleCancelRequest(submittedReceipt.id || submittedReceipt.requestId)}
                  disabled={cancelling}
                >
                  {cancelling ? "Reversing..." : "Cancel Request (Keep Account)"}
                </button>
                <Link
                  to="/"
                  className="privacy-switch-btn"
                  style={{ flex: 1, minWidth: "160px", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}
                >
                  Return to Home
                </Link>
              </div>
            </div>
          ) : activeRequest ? (
            /* ─── State: Active Request Found (In-Progress) ─── */
            <div>
              <div className="privacy-card-header">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px", marginBottom: "8px" }}>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", color: "var(--clr-danger)", fontWeight: 700, fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    <WarningIcon size={16} />
                    Active Request Underway
                  </div>
                  <span className="privacy-verified-pill" style={{ background: "#f9dedc", color: "#b3261e" }}>
                    Pending Erasure
                  </span>
                </div>
                <h2>Account Scheduled for Deletion</h2>
                <p>
                  An active deletion request is currently registered for <strong>{user?.email}</strong>.
                </p>
              </div>

              {/* Receipt Metadata */}
              <div className="privacy-receipt-meta">
                <div className="privacy-meta-row">
                  <span className="privacy-meta-label">Reference ID</span>
                  <span className="privacy-meta-val">
                    <span className="privacy-id-tag">
                      {activeRequest.id || activeRequest.requestId}
                    </span>
                    <button 
                      type="button" 
                      className="privacy-copy-btn"
                      onClick={() => handleCopyId(activeRequest.id || activeRequest.requestId)}
                    >
                      {copiedId === (activeRequest.id || activeRequest.requestId) ? "Copied!" : "Copy"}
                    </button>
                  </span>
                </div>
                <div className="privacy-meta-row">
                  <span className="privacy-meta-label">Requested On</span>
                  <span className="privacy-meta-val">
                    {formatTimestamp(activeRequest.createdAt || activeRequest.requestDate)}
                  </span>
                </div>
                <div className="privacy-meta-row">
                  <span className="privacy-meta-label">Grace Period Window</span>
                  <span className="privacy-meta-val" style={{ color: "var(--clr-warning)" }}>
                    {activeRequest.gracePeriodDays ?? 7} Days
                  </span>
                </div>
                {activeRequest.reason && (
                  <div className="privacy-meta-row">
                    <span className="privacy-meta-label">Stated Reason</span>
                    <span className="privacy-meta-val" style={{ maxWidth: "260px", textAlign: "right" }}>
                      "{activeRequest.reason}"
                    </span>
                  </div>
                )}
              </div>

              <div className="privacy-callout info">
                <div className="privacy-callout-icon">
                  <ShieldIcon size={20} style={{ color: "#00668b" }} />
                </div>
                <div className="privacy-callout-content">
                  <strong>Retain Your Account & Statistics</strong>
                  Changed your mind? You can retract this deletion request immediately. Your player profile, rankings, quiz points, and preferences will be preserved without interruption.
                </div>
              </div>

              <button
                type="button"
                className="privacy-btn-success"
                onClick={() => handleCancelRequest(activeRequest.id || activeRequest.requestId)}
                disabled={cancelling}
              >
                {cancelling ? (
                  <>
                    <Spinner />
                    <span>Cancelling Request...</span>
                  </>
                ) : (
                  <>
                    <CheckCircleIcon size={18} />
                    <span>Retract Request & Keep My Account</span>
                  </>
                )}
              </button>
            </div>
          ) : !user ? (
            /* ─── State: Unauthenticated (Verify Identity) ─── */
            <div>
              <div className="privacy-card-header">
                <h2>
                  <ShieldIcon size={22} style={{ color: "var(--clr-primary)" }} />
                  Verify Account Ownership
                </h2>
                <p>
                  To protect our players and prevent unauthorized deletion requests, identity verification with your Google account is required before submitting.
                </p>
              </div>

              <div className="privacy-callout warning">
                <div className="privacy-callout-icon">
                  <WarningIcon size={20} style={{ color: "var(--clr-primary)" }} />
                </div>
                <div className="privacy-callout-content">
                  <strong>Permanent Erasure Notice</strong>
                  Submitting an account deletion request permanently wipes your player profile, quiz results, contest medals, and leaderboard standings following the 7-day grace period.
                </div>
              </div>

              <div style={{ margin: "32px 0 24px 0" }}>
                <button
                  type="button"
                  className="privacy-btn-google-auth"
                  onClick={handleGoogleSignIn}
                >
                  <GoogleIcon size={22} />
                  <span>Continue with Google Account</span>
                </button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginTop: "24px" }}>
                <div style={{ background: "var(--clr-surface-container)", padding: "12px 14px", borderRadius: "10px", fontSize: "var(--text-xs)", display: "flex", alignItems: "center", gap: "8px" }}>
                  <span>🔒</span>
                  <span>End-to-end token verification</span>
                </div>
                <div style={{ background: "var(--clr-surface-container)", padding: "12px 14px", borderRadius: "10px", fontSize: "var(--text-xs)", display: "flex", alignItems: "center", gap: "8px" }}>
                  <span>🛡️</span>
                  <span>7-day safety grace window</span>
                </div>
              </div>
            </div>
          ) : (
            /* ─── State: Authenticated & Ready to Submit ─── */
            <div>
              <div className="privacy-card-header">
                <h2>
                  <TrashIcon size={22} style={{ color: "var(--clr-primary)" }} />
                  Submit Deletion Request
                </h2>
                <p>
                  Review your verified account details, optionally provide feedback, and confirm your request.
                </p>
              </div>

              {/* Verified Account Card */}
              <div className="privacy-user-card">
                <div className="privacy-user-details">
                  {user.photoURL ? (
                    <img src={user.photoURL} alt={user.displayName || "User"} className="privacy-user-avatar" />
                  ) : (
                    <div className="privacy-avatar-initial">
                      {(user.displayName || user.email || "U").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="privacy-user-name">
                      <span className="privacy-user-name-text">{user.displayName || "Bible Contest Player"}</span>
                      <span className="privacy-verified-pill">✓ Verified</span>
                    </div>
                    <div className="privacy-user-email">{user.email}</div>
                  </div>
                </div>

                <button
                  type="button"
                  className="privacy-switch-btn"
                  onClick={handleSwitchAccount}
                >
                  <span className="desktop-only-inline">Switch Account</span>
                  <span className="mobile-only-inline">Switch</span>
                </button>
              </div>

              {/* Quick Feedback Chips (2-column grid on mobile) */}
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "var(--text-xs)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--clr-text-2)", marginBottom: "8px" }}>
                  Select Reason (Optional)
                </label>
                <div className="privacy-chips-group">
                  {QUICK_REASONS.map((chip) => (
                    <button
                      key={chip.id}
                      type="button"
                      className={`privacy-reason-chip ${selectedChip === chip.desktop ? "selected" : ""}`}
                      onClick={() => handleSelectChip(chip.desktop)}
                    >
                      <span className="desktop-only-inline">{chip.desktop}</span>
                      <span className="mobile-only-inline">{chip.mobile}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Textarea for additional explanation */}
              <div className="privacy-textarea-wrap">
                <label>Additional Feedback or Reason</label>
                <textarea
                  className="privacy-input-textarea"
                  rows={2}
                  maxLength={500}
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    if (selectedChip && e.target.value !== selectedChip) {
                      setSelectedChip("");
                    }
                  }}
                  placeholder="Tell us what we could improve (optional)..."
                />
                <div className="privacy-char-counter">
                  {reason.length} / 500 characters
                </div>
              </div>

              {/* Callout Disclosure */}
              <div className="privacy-callout info">
                <div className="privacy-callout-icon">
                  <ShieldIcon size={18} style={{ color: "var(--clr-info)" }} />
                </div>
                <div className="privacy-callout-content">
                  <strong>7-Day Safety Grace Window:</strong> Once registered, you have 7 full days to cancel this request and retain your account without any data loss.
                </div>
              </div>

              {/* Legal Consent Checkbox */}
              <div className="privacy-consent-box">
                <label className="privacy-checkbox-label">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                  />
                  <span>
                    I understand that my player profile, contest participation records, and quiz statistics will be permanently erased following the 7-day grace period.
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="button"
                className="privacy-btn-danger"
                onClick={handleSubmitRequest}
                disabled={!consent || submitting}
              >
                {submitting ? (
                  <>
                    <Spinner />
                    <span>Registering Deletion Request...</span>
                  </>
                ) : (
                  <>
                    <TrashIcon size={18} />
                    <span>Submit Account Deletion Request</span>
                  </>
                )}
              </button>
            </div>
          )}
        </section>

        {/* ─── Sidebar: Guidance & Rights ─── */}
        <aside className="privacy-sidebar">
          
          {/* Card 1: Data Erasure Breakdown */}
          <div className="privacy-sidebar-card">
            <button
              type="button"
              className="privacy-sidebar-toggle-btn"
              onClick={() => toggleMobileSection("scope")}
            >
              <h3>
                <span>📋</span> Data Erasure Scope
              </h3>
              <span className="privacy-sidebar-chevron mobile-only-inline">
                {mobileSections.scope ? "−" : "+"}
              </span>
            </button>

            <div className={`privacy-sidebar-body ${mobileSections.scope ? "is-expanded" : ""}`}>
              <ul className="privacy-data-list">
                <li className="privacy-data-item">
                  <span className="privacy-data-icon danger">✕</span>
                  <div>
                    <strong>Permanently Removed:</strong> Personal profile, email address, display name, avatar, and individual quiz answer submissions.
                  </div>
                </li>
                <li className="privacy-data-item">
                  <span className="privacy-data-icon danger">✕</span>
                  <div>
                    <strong>Forfeited Assets:</strong> Leaderboard points, historical tournament rankings, and prize eligibility.
                  </div>
                </li>
                <li className="privacy-data-item">
                  <span className="privacy-data-icon safe">✓</span>
                  <div>
                    <strong>Retained (Anonymized):</strong> High-level non-identifiable contest statistics for system integrity and abuse detection.
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Card 2: Frequently Asked Questions */}
          <div className="privacy-sidebar-card">
            <button
              type="button"
              className="privacy-sidebar-toggle-btn"
              onClick={() => toggleMobileSection("faqs")}
            >
              <h3>
                <span>💬</span> Privacy FAQs
              </h3>
              <span className="privacy-sidebar-chevron mobile-only-inline">
                {mobileSections.faqs ? "−" : "+"}
              </span>
            </button>

            <div className={`privacy-sidebar-body ${mobileSections.faqs ? "is-expanded" : ""}`}>
              <div>
                {FAQS.map((faq, index) => {
                  const isOpen = openFaq === index;
                  return (
                    <div key={index} className="privacy-accordion-item">
                      <button
                        type="button"
                        className="privacy-accordion-header"
                        onClick={() => setOpenFaq(isOpen ? -1 : index)}
                      >
                        <span>{faq.q}</span>
                        <span>{isOpen ? "−" : "+"}</span>
                      </button>
                      {isOpen && (
                        <div className="privacy-accordion-body">
                          {faq.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Card 3: Standards & Direct Contact */}
          <div className="privacy-sidebar-card" style={{ background: "#faf7f4" }}>
            <button
              type="button"
              className="privacy-sidebar-toggle-btn"
              onClick={() => toggleMobileSection("compliance")}
            >
              <h3 style={{ fontSize: "0.95rem", margin: 0 }}>
                <span>🛡️</span> Standards & Compliance
              </h3>
              <span className="privacy-sidebar-chevron mobile-only-inline">
                {mobileSections.compliance ? "−" : "+"}
              </span>
            </button>

            <div className={`privacy-sidebar-body ${mobileSections.compliance ? "is-expanded" : ""}`}>
              <p style={{ fontSize: "var(--text-xs)", color: "var(--clr-text-2)", margin: "10px 0 12px 0", lineHeight: 1.5 }}>
                Engineered according to Google Play Data Safety requirements, Apple App Store Guideline 5.1.1(v), and GDPR Article 17 (Right to Erasure).
              </p>

              <div className="privacy-trust-badges">
                <span className="privacy-badge-pill">🔒 SSL 256-Bit</span>
                <span className="privacy-badge-pill">📱 Play Store Safe</span>
                <span className="privacy-badge-pill">🍏 App Store 5.1.1(v)</span>
              </div>

              <div style={{ marginTop: "16px", paddingTop: "12px", borderTop: "1px solid var(--clr-border)", fontSize: "var(--text-xs)" }}>
                Direct questions to:{" "}
                <a href="mailto:support@biblecontest.app" style={{ color: "var(--clr-primary)", fontWeight: 700 }}>
                  support@biblecontest.app
                </a>
              </div>
            </div>
          </div>
        </aside>

      </main>

      {/* ─── Portal Footer ─── */}
      <footer className="privacy-portal-footer">
        <div className="privacy-footer-inner">
          <div>
            © {new Date().getFullYear()} Bible Contest Ministry. Dedicated to Scripture engagement.
          </div>
          <div className="privacy-footer-links">
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-and-conditions">Terms of Service</Link>
            <Link to="/support">Support Center</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UserDeletionRequest;
